import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "../../hooks/useAuth";
import useIsMobile from "../../hooks/useIsMobile";
import useTenantModules from "../../hooks/useTenantModules";
import { getMenuSections } from "../../config/menuConfig";

// Identidad estable para "sin módulos": como dependencia del useMemo, un [] nuevo
// en cada render recalcularía las secciones siempre.
const NO_TENANT_MODULES = [];

// Títulos para rutas que NO viven en el sidebar (se abren desde el menú de
// usuario del header): cuenta y configuración de empresa.
const OFF_SIDEBAR_TITLES = [
  { path: "/admin/settings", labelKey: "Configuración de empresa", icon: "settings" },
  { path: "/admin/my-account", labelKey: "Mi cuenta", icon: "person" },
  { path: "/home/my-account", labelKey: "Mi cuenta", icon: "person" },
  { path: "/home/configuracion-empresa", labelKey: "Configuración de empresa", icon: "settings" },
];

// Header de la app. El switcher "Aplicaciones" se eliminó: toda la navegación
// vive ahora en el sidebar (ver menuConfig.js). El header solo muestra el título
// de la pantalla actual (derivado del ítem de menú que matchea la ruta) y, a la
// derecha, los controles que se le pasen como children (ej. ThemeToggle).
export default function NewAdminMenu({ children }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  // Mismo corte con el que la vista decide su diseño móvil (useIsMobile(768)
  // en pages/helpdeskIncidents): el título corto aparece justo cuando la
  // pantalla se arma en móvil, ni antes ni después.
  const isMobile = useIsMobile(768);
  // Mismas secciones que el sidebar, incluidos los ítems por módulo de la
  // empresa: sin ellos, en /admin/adminRoute el header no tendría su título.
  const { status: tenantModulesStatus, modules, tenantType } = useTenantModules();
  const tenantModules = tenantModulesStatus === "ready" ? modules : NO_TENANT_MODULES;

  const sections = useMemo(
    () =>
      getMenuSections(user?.role, null, {
        hasOwnFleet: user?.company?.hasOwnFleet,
        tenantModules,
        tenantType,
      }),
    [user, tenantModules, tenantType]
  );

  // Ítem de menú que mejor matchea la ruta actual (match más específico gana).
  const current = useMemo(() => {
    let best = null;
    for (const section of sections) {
      for (const item of section.items) {
        const matches =
          pathname === item.path || pathname.startsWith(item.path + "/");
        if (matches && (!best || item.path.length > best.item.path.length)) {
          best = { section, item };
        }
      }
    }
    return best;
  }, [sections, pathname]);

  // Rutas fuera del sidebar (cuenta / configuración) tienen su propio título.
  const offSidebar = useMemo(
    () =>
      OFF_SIDEBAR_TITLES.find(
        (entry) => pathname === entry.path || pathname.startsWith(entry.path + "/")
      ),
    [pathname]
  );

  // offSidebar gana: si no, "/admin/settings" caería en el match genérico "/admin" (Pedidos).
  const rawTitle = offSidebar?.labelKey || current?.item.label || "Beartrack";
  // Título corto opcional del ítem (`shortTitle` en menuConfig), solo en móvil.
  // No aplica si gana una ruta fuera del sidebar: ahí el título no es del ítem.
  const shortTitle = isMobile && !offSidebar ? current?.item.shortTitle : undefined;
  const title = shortTitle
    ? t(shortTitle.key, { defaultValue: shortTitle.defaultValue })
    : t(rawTitle);
  const icon = offSidebar?.icon || current?.section.icon || "dashboard";

  // Antes usábamos <Navbar expand="md"> de reactstrap, que en mobile (<768px)
  // colapsaba los controles del header detrás del sistema de Bootstrap. Como
  // no queremos un toggle Bootstrap (ya tenemos el hamburger del sidebar +
  // bottom-nav), reemplazamos por un flex plano + Tailwind. Resultado: los
  // controles siempre visibles, espaciado tight en mobile.
  return (
    <div className="new-admin-menu-container w-full flex items-center justify-between gap-2">
      <span className="new-admin-menu-brand">
        <span className="material-symbols-outlined new-admin-menu-brand__icon" aria-hidden="true">
          {icon}
        </span>
        <span className="new-admin-menu-brand__title">{title}</span>
      </span>

      {/* Controles de la derecha (Language, Theme, Bell, UserMenu) */}
      <div className="new-admin-menu-actions flex items-center gap-1 sm:gap-2 shrink-0">
        {children}
      </div>
    </div>
  );
}
